import { sampleQueryKeys } from '@/config/queryKeys';
import { SampleDetailsPage } from '@/pages/Samples/components/SampleDetailsPage';
import { useOrgId } from '@/pages/Samples/helpers';
import { getSample } from '@/services/samples';
import type { SampleWithNamesPublic } from '@/types';
import { isForbiddenError, isUnauthorizedError } from '@/types/errors';
import { FullLoading } from '@components/index';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useQuery } from '@tanstack/react-query';
import { createLazyFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createLazyFileRoute('/samples/$sampleId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { sampleId } = Route.useParams();
  const orgId = useOrgId();
  const { t } = useTranslation('studies');
  const { t: tLogin } = useTranslation('login');
  const navigate = useNavigate();

  // The loader seeds this cache entry from the AG Grid list cache for an instant render when
  // navigating from the samples list. On a hard refresh that in-memory cache is empty, so the
  // query fetches the sample by id via getSample (which works around the missing single-sample
  // GET by querying the org-level search endpoint with an id-equals filter).
  const hasOrg = typeof orgId === 'string' && orgId !== '';
  const {
    data: sample,
    isPending,
    isError,
    error,
  } = useQuery<SampleWithNamesPublic>({
    queryKey: sampleQueryKeys.detail(orgId ?? '', sampleId),
    queryFn: () => getSample(orgId ?? '', sampleId),
    enabled: hasOrg,
    staleTime: Number.POSITIVE_INFINITY,
    // Don't retry session-expiry/permission failures — they're terminal for the
    // current session and retrying only delays the global 401 redirect (401) or
    // the /unauthorized navigation (403) below.
    retry: (failureCount, err) => (isUnauthorizedError(err) ? false : failureCount < 3),
  });

  // Only 403 should land on /unauthorized, matching the existing convention in
  // src/pages/Organizations/index.tsx and src/routes/users/index.tsx. 401 means
  // the session expired and ApiClient's global onUnauthorized handler (see
  // src/lib/unauthorized-handler.ts) is already navigating to /login; racing it
  // with a /unauthorized push here would strand the user on the wrong page.
  useEffect(() => {
    if (error && isForbiddenError(error)) {
      void navigate({ to: '/unauthorized' });
    }
  }, [error, navigate]);

  // A 401 here means the session expired mid-visit (e.g. while viewing the
  // Samples list). ApiClient already triggers the global redirect-to-login flow
  // for this response — rendering the generic "not found" message would be
  // misleading and would race that redirect, so render nothing and let the app
  // navigate away. A 403 is handled by the /unauthorized navigation above, which
  // only fires from a useEffect — suppress the render here too so the generic
  // "not found" text doesn't flash before that navigation happens.
  if (isError && isUnauthorizedError(error)) {
    return null;
  }

  if (isError) {
    return <Text as="span">{t('samples.details.notFound')}</Text>;
  }

  if (!hasOrg || isPending || sample === undefined) {
    return <FullLoading label={tLogin('loading.default')} />;
  }

  return <SampleDetailsPage orgId={orgId} sample={sample} />;
}
